import {
  effectiveUnitPrice,
  sumCartCatalogGrossTotals,
  sumCartCatalogTotals,
} from "./effectivePrice";

export type ShopCheckoutTotals = {
  orderAmount: number;
  discountAmount: number;
};

function sumShopResponseLineTotals(
  result: Record<string, unknown>
): number | null {
  const groups = [
    result.services,
    result.products,
    result.lines,
    result.items,
    result.order_lines,
  ];
  let total = 0;
  let count = 0;
  for (const group of groups) {
    if (!Array.isArray(group)) continue;
    for (const line of group) {
      if (line == null || typeof line !== "object") continue;
      const row = line as Record<string, unknown>;
      const qty = Number(row.qty ?? row.quantity ?? 1) || 1;
      const unit = effectiveUnitPrice(row);
      if (unit > 0) {
        total += qty * unit;
        count += 1;
      }
    }
  }
  return count > 0 ? total : null;
}

/** Parse shop/coupon API payload — net total is `order_amount`, not gross `amount`. */
export function parseShopCheckoutTotals(
  result: Record<string, unknown> | null | undefined
): ShopCheckoutTotals | null {
  if (!result) return null;

  const discountRaw = result.discount_amount;
  const parsedDiscount =
    discountRaw != null && discountRaw !== "" ? Number(discountRaw) : 0;
  const discountAmount = Number.isFinite(parsedDiscount) ? parsedDiscount : 0;

  let headerAmount: number | null = null;

  const orderRaw = result.order_amount;
  if (orderRaw != null && orderRaw !== "") {
    const orderAmount = Number(orderRaw);
    if (Number.isFinite(orderAmount)) {
      headerAmount = orderAmount;
    }
  }

  if (headerAmount == null) {
    const grossRaw =
      result.amount ?? result.total_amount ?? result.total;
    if (grossRaw != null && grossRaw !== "") {
      const gross = Number(grossRaw);
      if (Number.isFinite(gross)) {
        headerAmount =
          discountAmount > 0
            ? Math.max(0, gross - discountAmount)
            : gross;
      }
    }
  }

  const lineTotal = sumShopResponseLineTotals(result);
  if (lineTotal != null) {
    if (headerAmount == null) {
      return { orderAmount: lineTotal, discountAmount };
    }
    if (lineTotal < headerAmount - 0.02) {
      return { orderAmount: lineTotal, discountAmount };
    }
  }

  if (headerAmount != null) {
    return { orderAmount: headerAmount, discountAmount };
  }

  return null;
}

/**
 * Prefer persisted/API order total; if missing or 0 while cart has priced lines, sum catalog (with discounts).
 */
export function resolveOrderBaseAmount(
  storedAmount: unknown,
  services: any[],
  products: any[],
  discountAmount?: unknown
): number {
  const stored = Number(storedAmount);
  const discount = Number(discountAmount);
  const cartNet = sumCartCatalogTotals(services, products);
  const cartGross = sumCartCatalogGrossTotals(services, products);

  if (Number.isFinite(stored) && stored > 0) {
    // Coupon applied on catalog list total (gross)
    if (
      Number.isFinite(discount) &&
      discount > 0 &&
      stored > discount &&
      cartGross > 0 &&
      Math.abs(stored - cartGross) < 0.02
    ) {
      const net = stored - discount;
      return net >= 0 ? net : stored;
    }

    // Shop API returned list/gross total but cart lines have sale prices
    if (cartNet > 0 && cartNet < stored - 0.02) {
      return cartNet;
    }

    // API net (e.g. after coupon) is lower than catalog
    if (cartNet > 0 && stored < cartNet - 0.02) {
      return stored;
    }

    return stored;
  }

  if (cartNet > 0) return cartNet;
  return Number.isFinite(stored) ? stored : 0;
}

/** Full payable total for UI and HyperPay — same inputs as checkout. */
export function getGrandPayableTotalFromState(
  storedAmount: unknown,
  donationAmount: unknown,
  walletAmount: unknown,
  services: any[],
  products: any[],
  discountAmount?: unknown
): number {
  const base = resolveOrderBaseAmount(
    storedAmount,
    services,
    products,
    discountAmount
  );
  return getGrandPayableTotal(base, donationAmount, walletAmount);
}

/**
 * Amount actually charged: order total + optional donation − wallet applied.
 * Matches HyperPay checkout and the checkout pay CTA.
 */
export function getGrandPayableTotal(
  orderAmount: unknown,
  donationAmount: unknown,
  walletAmount: unknown
): number {
  const base = Number(orderAmount);
  const donation = Number(donationAmount);
  const wallet = Number(walletAmount);
  const a = Number.isFinite(base) ? base : 0;
  const d = Number.isFinite(donation) ? donation : 0;
  const w = Number.isFinite(wallet) ? wallet : 0;
  const total = a + d - w;
  if (!Number.isFinite(total)) return 0;
  return Math.max(0, total);
}
